import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Collapse,
  Fade,
  Grid,
  LinearProgress,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { AccessTime, Person, Business } from '@mui/icons-material';
import { Link as RouterLink, useParams } from 'react-router-dom';
import api from '../services/api';
import DashboardTrendChart from '../components/dashboard/DashboardTrendChart';
import {
  useUserPreferences,
  PREFERENCE_KEYS,
  DEFAULT_PREFERENCES
} from '../hooks/useUserPreferences';
import {
  getCurrentWeekDates,
  getLastWeekDates,
  getCurrentMonthDates,
  getLastMonthDates,
  type DateRange
} from '../utils/dashboardDates';
import {
  sumPlannedHoursForRange,
  normalizePersonalWorkingHoursSchedule,
  type PersonalWorkingHoursSchedule
} from '../utils/personalWorkingHours';
import {
  filterEntriesByOrganization,
  groupWorkedHoursByOrganization,
  sumWorkedHours,
  type TimesheetEntryLike
} from '../utils/timesheetHours';
import { buildTrendQueryParams, getTodayAnchor, shiftTrendAnchor } from '../utils/dashboardTrendDates';
import type { DashboardTrendResponse, TrendGranularity } from '../types/dashboardTrend';

export type DashboardLens = 'personal' | 'org';

const LENS_STORAGE_KEY = 'dashboardLens';

type PeriodKey = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth';

interface PeriodStats {
  worked: number;
  planned: number;
}

interface OrgHoursRow {
  orgId: string;
  name: string;
  hours: number;
}

interface LensCache {
  periodStats: Record<PeriodKey, PeriodStats>;
  hoursByOrg: OrgHoursRow[];
  ready: boolean;
}

const PERIOD_LABELS: Record<PeriodKey, string> = {
  thisWeek: 'This week',
  lastWeek: 'Last week',
  thisMonth: 'This month',
  lastMonth: 'Last month'
};

const PERIOD_RANGES: Record<PeriodKey, (timeZone: string) => DateRange> = {
  thisWeek: getCurrentWeekDates,
  lastWeek: getLastWeekDates,
  thisMonth: getCurrentMonthDates,
  lastMonth: getLastMonthDates
};

const PERIOD_KEYS = Object.keys(PERIOD_LABELS) as PeriodKey[];

function emptyPeriodStats(): Record<PeriodKey, PeriodStats> {
  return {
    thisWeek: { worked: 0, planned: 0 },
    lastWeek: { worked: 0, planned: 0 },
    thisMonth: { worked: 0, planned: 0 },
    lastMonth: { worked: 0, planned: 0 }
  };
}

function emptyLensCache(): LensCache {
  return { periodStats: emptyPeriodStats(), hoursByOrg: [], ready: false };
}

function readStoredLens(): DashboardLens {
  const stored = localStorage.getItem(LENS_STORAGE_KEY);
  return stored === 'personal' || stored === 'org' ? stored : 'org';
}

function formatHours(h: number): string {
  return h.toFixed(1);
}

function progressValue(worked: number, planned: number): number {
  if (planned <= 0) return worked > 0 ? 100 : 0;
  return Math.min(100, (worked / planned) * 100);
}

function sumExpectedFromResolve(days: { expectedHours?: number }[] | undefined): number {
  if (!days?.length) return 0;
  return days.reduce((sum, d) => sum + (Number(d.expectedHours) || 0), 0);
}

interface PeriodCardProps {
  label: string;
  worked: number;
  planned: number;
  plannedLabel: string;
  loading?: boolean;
}

const PeriodCard: React.FC<PeriodCardProps> = ({
  label,
  worked,
  planned,
  plannedLabel,
  loading = false
}) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        {label}
      </Typography>
      {loading ? (
        <>
          <Skeleton variant="text" width="55%" height={36} />
          <Skeleton variant="text" width="40%" sx={{ mt: 0.5 }} />
          <Skeleton variant="rounded" height={6} sx={{ mt: 1.5, borderRadius: 1 }} />
        </>
      ) : (
        <>
          <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
            {formatHours(worked)}
            <Typography component="span" variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>
              /
            </Typography>
            {formatHours(planned)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            logged / {plannedLabel}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressValue(worked, planned)}
            sx={{ mt: 1.5, height: 6, borderRadius: 1 }}
          />
        </>
      )}
    </CardContent>
  </Card>
);

const OrganizationDashboard: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [lens, setLens] = useState<DashboardLens>(readStoredLens);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [orgName, setOrgName] = useState<string>('');
  const [cache, setCache] = useState<Record<DashboardLens, LensCache>>({
    personal: emptyLensCache(),
    org: emptyLensCache()
  });
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>('day');
  const [trendAnchor, setTrendAnchor] = useState<string>(getTodayAnchor());
  const [trendData, setTrendData] = useState<DashboardTrendResponse | null>(null);
  const [trendLoading, setTrendLoading] = useState<boolean>(true);
  const hasLoadedOnceRef = useRef(false);

  const { getPreference, loading: preferencesLoading } = useUserPreferences();

  useEffect(() => {
    setCache({ personal: emptyLensCache(), org: emptyLensCache() });
    setTrendAnchor(getTodayAnchor());
    setTrendData(null);
    hasLoadedOnceRef.current = false;
    setInitialLoading(true);
    setRefreshing(false);
  }, [organizationId]);

  useEffect(() => {
    let cancelled = false;
    const loadTrend = async () => {
      setTrendLoading(true);
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        const response = await api.getDashboardTrend(
          buildTrendQueryParams({
            lens,
            granularity: trendGranularity,
            anchor: trendAnchor,
            timezone,
            organizationId
          })
        );
        if (!cancelled && response?.success) {
          setTrendData(response.data);
        }
      } catch (err) {
        console.error('Trend load error:', err);
      } finally {
        if (!cancelled) setTrendLoading(false);
      }
    };
    loadTrend();
    return () => {
      cancelled = true;
    };
  }, [lens, trendGranularity, trendAnchor, organizationId]);

  const personalSchedule = useMemo((): PersonalWorkingHoursSchedule => {
    const key = PREFERENCE_KEYS.TIMESHEET_DEFAULT_WORKING_HOURS;
    const raw = getPreference(key, DEFAULT_PREFERENCES[key]);
    return normalizePersonalWorkingHoursSchedule(raw);
  }, [getPreference]);

  const handleLensChange = (_: React.MouseEvent<HTMLElement>, value: DashboardLens | null) => {
    if (value) {
      setLens(value);
      localStorage.setItem(LENS_STORAGE_KEY, value);
    }
  };

  const loadEntriesForRange = useCallback(async (range: DateRange, timezone: string) => {
    const response = await api.getTimesheetEntries({
      startDate: range.from,
      endDate: range.to,
      timezone
    });
    return (response.data || []) as TimesheetEntryLike[];
  }, []);

  const loadOrgExpectedForRange = useCallback(
    async (
      range: DateRange,
      orgId: string,
      uid: string,
      timezone: string
    ): Promise<number> => {
      const resolved = await api.resolveWorkingCalendar(orgId, {
        userId: uid,
        from: range.from,
        to: range.to,
        timezone
      });
      return sumExpectedFromResolve(resolved?.data?.days);
    },
    []
  );

  const buildOrgBreakdown = useCallback(
    async (weekEntries: TimesheetEntryLike[]): Promise<OrgHoursRow[]> => {
      const grouped = groupWorkedHoursByOrganization(weekEntries);
      const orgsRes = await api.getUserOrganizations();
      const orgList: { id: string; name: string }[] = [];
      if (orgsRes?.success && Array.isArray(orgsRes.data)) {
        for (const row of orgsRes.data) {
          const id = row.organizationId || row.organization?.id;
          const name = row.organization?.name || 'Organization';
          if (id) orgList.push({ id, name });
        }
      }

      const breakdown = orgList
        .map((o) => ({
          orgId: o.id,
          name: o.name,
          hours: grouped.get(o.id) || 0
        }))
        .filter((row) => row.hours > 0)
        .sort((a, b) => b.hours - a.hours);

      const knownIds = new Set(orgList.map((o) => o.id));
      Array.from(grouped.entries()).forEach(([orgId, hours]) => {
        if (orgId === '_unknown' || knownIds.has(orgId)) return;
        breakdown.push({ orgId, name: 'Other', hours });
      });

      return breakdown;
    },
    []
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (preferencesLoading) return;

      if (hasLoadedOnceRef.current) {
        setRefreshing(true);
      } else {
        setInitialLoading(true);
      }
      setError(null);

      try {
        const profile = await api.getProfile();
        const uid = profile?.data?.user?.id;
        if (!uid) {
          throw new Error('Could not load user profile');
        }
        if (cancelled) return;
        setUserId(uid);

        const orgNamePromise = organizationId
          ? api
              .getOrganization(organizationId)
              .then((orgRes) => {
                const data = orgRes?.data ?? orgRes;
                return typeof data?.name === 'string' ? data.name : 'Organization';
              })
              .catch(() => 'Organization')
          : Promise.resolve('');

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

        const periodData = await Promise.all(
          PERIOD_KEYS.map(async (key) => {
            const range = PERIOD_RANGES[key](timezone);
            const entries = await loadEntriesForRange(range, timezone);
            return { key, range, entries };
          })
        );

        if (cancelled) return;

        const personalStats = emptyPeriodStats();
        for (const { key, range, entries } of periodData) {
          personalStats[key] = {
            worked: sumWorkedHours(entries),
            planned: sumPlannedHoursForRange(personalSchedule, range.from, range.to)
          };
        }

        const thisWeekEntries =
          periodData.find((p) => p.key === 'thisWeek')?.entries ?? [];
        const hoursByOrg = await buildOrgBreakdown(thisWeekEntries);

        if (cancelled) return;
        setCache((prev) => ({
          ...prev,
          personal: { periodStats: personalStats, hoursByOrg, ready: true }
        }));

        const orgStats = emptyPeriodStats();
        if (organizationId) {
          const expectedResults = await Promise.all(
            periodData.map(({ range }) =>
              loadOrgExpectedForRange(range, organizationId, uid, timezone)
            )
          );
          if (cancelled) return;
          periodData.forEach(({ key, entries }, index) => {
            orgStats[key] = {
              worked: sumWorkedHours(filterEntriesByOrganization(entries, organizationId)),
              planned: expectedResults[index]
            };
          });
        }

        const resolvedOrgName = await orgNamePromise;
        if (cancelled) return;
        setOrgName(resolvedOrgName);
        setCache((prev) => ({
          ...prev,
          org: {
            periodStats: orgStats,
            hoursByOrg: [],
            ready: true
          }
        }));
      } catch (err: unknown) {
        console.error('Dashboard load error:', err);
        if (!cancelled) {
          const message =
            err && typeof err === 'object' && 'response' in err
              ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
              : null;
          setError(message || 'Failed to load dashboard');
        }
      } finally {
        if (!cancelled) {
          hasLoadedOnceRef.current = true;
          setInitialLoading(false);
          setRefreshing(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when org/schedule changes, not on lens toggle
  }, [
    organizationId,
    preferencesLoading,
    personalSchedule,
    loadEntriesForRange,
    loadOrgExpectedForRange,
    buildOrgBreakdown
  ]);

  const activeCache = cache[lens];
  const contentLoading =
    preferencesLoading || initialLoading || (!activeCache.ready && refreshing);
  const showRefreshBar = refreshing && activeCache.ready;

  const plannedLabel = lens === 'personal' ? 'planned' : 'expected';
  const hoursByOrg = lens === 'personal' ? activeCache.hoursByOrg : [];

  return (
    <Box sx={{ p: 3, position: 'relative' }}>
      {showRefreshBar && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            borderRadius: 0
          }}
        />
      )}

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2
        }}
      >
        <Typography variant="h4" component="h1">
          Dashboard
        </Typography>
        <ToggleButtonGroup
          value={lens}
          exclusive
          onChange={handleLensChange}
          size="small"
          aria-label="Dashboard view"
          sx={{ minWidth: 220 }}
        >
          <ToggleButton value="personal" aria-label="Personal view" sx={{ flex: 1 }}>
            <Person sx={{ mr: 0.75, fontSize: 18 }} />
            Personal
          </ToggleButton>
          <ToggleButton value="org" aria-label="Organization view" sx={{ flex: 1 }}>
            <Business sx={{ mr: 0.75, fontSize: 18 }} />
            Organization
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Fade in appear key={`hero-${lens}`} timeout={220}>
        <Box>
          <DashboardTrendChart
            data={trendData}
            loading={trendLoading}
            granularity={trendGranularity}
            onGranularityChange={(next) => {
              setTrendGranularity(next);
              setTrendAnchor(getTodayAnchor());
            }}
            onPrev={() => setTrendAnchor((prev) => shiftTrendAnchor(prev, trendGranularity, -1))}
            onNext={() => setTrendAnchor((prev) => shiftTrendAnchor(prev, trendGranularity, 1))}
          />
        </Box>
      </Fade>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {PERIOD_KEYS.map((key) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <Fade in appear key={`${lens}-${key}`} timeout={220}>
              <Box>
                <PeriodCard
                  label={PERIOD_LABELS[key]}
                  worked={activeCache.periodStats[key].worked}
                  planned={activeCache.periodStats[key].planned}
                  plannedLabel={plannedLabel}
                  loading={contentLoading}
                />
              </Box>
            </Fade>
          </Grid>
        ))}
      </Grid>

      <Collapse in={lens === 'personal'} timeout={280} unmountOnExit>
        <Card variant="outlined" sx={{ mb: lens === 'org' ? 0 : undefined }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <AccessTime color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">This week by organization</Typography>
            </Box>
            {contentLoading ? (
              <>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width="55%" sx={{ mt: 1 }} />
              </>
            ) : hoursByOrg.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No hours logged this week yet.
              </Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {hoursByOrg.map((row) => (
                  <Box
                    component="li"
                    key={row.orgId}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      py: 0.75,
                      listStyle: 'disc'
                    }}
                  >
                    <Typography variant="body2">{row.name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatHours(row.hours)} h
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Collapse>

      <Collapse in={lens === 'org' && Boolean(organizationId && userId)} timeout={280} unmountOnExit>
        <Typography variant="body2" sx={{ mt: lens === 'personal' ? 0 : 0 }}>
          <RouterLink to={`/org/${organizationId}/timesheets/calendar`}>
            Open timesheet for {orgName || 'this organization'}
          </RouterLink>
        </Typography>
      </Collapse>
    </Box>
  );
};

export default OrganizationDashboard;
